import type {
  AcademicUnitSummary,
  BreakdownItem,
  EngagementFilters,
  EngagementOption,
  EngagementSnapshot,
  EngagementStatus,
  EngagementStudent,
  EngagementSummary,
  NumericValue
} from "./types";

export const EMPTY_ENGAGEMENT_FILTERS: EngagementFilters = {
  periodo: "",
  polo: "",
  curso: "",
  ua: ""
};

export const STATUS_COLORS: Record<EngagementStatus, string> = {
  Ativo: "#0f8a69",
  Suporte: "#d99a1b",
  "Suporte Crítico": "#c8102e",
  "Nunca Acessou": "#c45f16",
  Cancelado: "#64748b",
  "Não sincronizado": "#7c3aed",
  Outros: "#94a3b8"
};

const STATUS_ORDER: EngagementStatus[] = [
  "Ativo",
  "Suporte",
  "Suporte Crítico",
  "Nunca Acessou",
  "Cancelado",
  "Não sincronizado",
  "Outros"
];

export const MIN_AGGREGATE_SIZE = 5;

export function snapshotRows(snapshot: EngagementSnapshot): EngagementStudent[] {
  return Array.isArray(snapshot.dados_agregados_alunos)
    ? snapshot.dados_agregados_alunos.filter((row) => row && typeof row === "object")
    : [];
}

export function normalizePeriod(value: unknown): string {
  const match = String(value ?? "").match(/(20\d{2})\D*([12])(?:\D|$)/);
  return match ? `${match[1]}/${match[2]}` : "";
}

export function studentStatus(row: EngagementStudent): EngagementStatus {
  const status = normalizeText(row.status);
  const situation = normalizeText(row.situacao);

  if (status === "ativo") return "Ativo";
  if (status === "suporte critico") return "Suporte Crítico";
  if (status === "suporte") return "Suporte";
  if (status === "nunca acessou") return "Nunca Acessou";
  if (status === "nao sincronizado") return "Não sincronizado";
  if (status === "cancelado" || situation.includes("cancelad")) return "Cancelado";
  return "Outros";
}

export function studentPeriods(row: EngagementStudent): string[] {
  const periods = new Set<string>();
  const membership = row.ingressante_nos_termos;

  if (membership && typeof membership === "object") {
    for (const [term, included] of Object.entries(membership)) {
      if (!isIncluded(included)) continue;
      const period = normalizePeriod(term);
      if (period) periods.add(period);
    }
  }

  [row.semestre_padronizado, row.semestre_ingresso].forEach((term) => {
    const period = normalizePeriod(term);
    if (period) periods.add(period);
  });

  return [...periods];
}

export function buildEngagementOptions(snapshot: EngagementSnapshot): Record<keyof EngagementFilters, EngagementOption[]> {
  const rows = snapshotRows(snapshot);
  const periods = new Set<string>();
  rows.forEach((row) => studentPeriods(row).forEach((period) => periods.add(period)));
  (snapshot.lista_periodos_letivos || []).forEach((term) => {
    const period = normalizePeriod(term);
    if (period) periods.add(period);
  });

  return {
    periodo: [...periods]
      .sort((a, b) => periodRank(b) - periodRank(a))
      .map(toOption),
    polo: privacySafeOptions(rows, studentPolo),
    curso: privacySafeOptions(rows, studentCourse),
    ua: privacySafeOptions(rows, studentUa)
  };
}

export function filterEngagementRows(rows: EngagementStudent[], filters: EngagementFilters): EngagementStudent[] {
  return rows.filter((row) => (
    (!filters.periodo || studentPeriods(row).includes(filters.periodo))
    && (!filters.polo || studentPolo(row) === filters.polo)
    && (!filters.curso || studentCourse(row) === filters.curso)
    && (!filters.ua || studentUa(row) === filters.ua)
  ));
}

export function buildEngagementSummary(rows: EngagementStudent[], generatedAt?: string): EngagementSummary {
  const total = rows.length;
  const counts = new Map<EngagementStatus, number>(STATUS_ORDER.map((status) => [status, 0]));
  rows.forEach((row) => {
    const status = studentStatus(row);
    counts.set(status, (counts.get(status) || 0) + 1);
  });

  const count = (status: EngagementStatus) => counts.get(status) || 0;
  const status = STATUS_ORDER
    .map((label) => ({ label, value: count(label), share: percent(count(label), total) }))
    .filter((item) => item.value > 0);

  return {
    total,
    active: count("Ativo"),
    support: count("Suporte") + count("Suporte Crítico"),
    criticalSupport: count("Suporte Crítico"),
    neverAccessed: count("Nunca Acessou"),
    cancelled: count("Cancelado"),
    notSynchronized: count("Não sincronizado"),
    other: count("Outros"),
    status,
    byUa: buildRankedBreakdown(rows, studentUa, total, 8),
    byCourse: buildRankedBreakdown(rows, studentCourse, total, 8),
    byPolo: buildRankedBreakdown(rows, studentPolo, total, 8),
    engagement: buildEngagementBuckets(rows),
    recency: buildRecencyBuckets(rows, generatedAt)
  };
}

export function buildAcademicUnitSummary(rows: EngagementStudent[]): AcademicUnitSummary[] {
  const groups = groupRows(rows, studentUa);
  return [...groups.entries()]
    .filter(([, group]) => group.length >= MIN_AGGREGATE_SIZE)
    .map(([label, group]) => {
      const total = group.length;
      const active = countStatus(group, "Ativo");
      const support = countStatus(group, "Suporte") + countStatus(group, "Suporte Crítico");
      const neverAccessed = countStatus(group, "Nunca Acessou");
      const cancelled = countStatus(group, "Cancelado");
      return {
        label,
        total,
        active,
        activeRate: percent(active, total),
        support,
        supportRate: percent(support, total),
        neverAccessed,
        neverAccessedRate: percent(neverAccessed, total),
        cancelled,
        cancelledRate: percent(cancelled, total)
      };
    })
    .sort((a, b) => b.total - a.total || localeSort(a.label, b.label));
}

export function buildRankedBreakdown(
  rows: EngagementStudent[],
  selector: (row: EngagementStudent) => string,
  denominator = rows.length,
  limit = 8
): BreakdownItem[] {
  const counts = [...groupRows(rows, selector).entries()]
    .map(([label, groupedRows]) => ({ label, value: groupedRows.length }))
    .sort((a, b) => b.value - a.value || localeSort(a.label, b.label));

  const visible = counts.filter((item) => item.value >= MIN_AGGREGATE_SIZE);
  const suppressed = counts.filter((item) => item.value < MIN_AGGREGATE_SIZE)
    .reduce((sum, item) => sum + item.value, 0);
  const top = visible.slice(0, limit);
  const remaining = visible.slice(limit).reduce((sum, item) => sum + item.value, 0) + suppressed;

  if (remaining > 0) {
    top.push({ label: "Demais grupos", value: remaining });
  }

  return top.map((item) => ({ ...item, share: percent(item.value, denominator) }));
}

export function studentPolo(row: EngagementStudent): string {
  return cleanText(row.polo, "Não informado");
}

export function studentCourse(row: EngagementStudent): string {
  return cleanText(row.turma, "Não informado");
}

export function studentUa(row: EngagementStudent): string {
  return cleanText(row.ua, "Não informado");
}

export function ratio(value: number, total: number): number {
  return percent(value, total);
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("pt-BR").format(value);
}

export function formatPercent(value: number, digits = 1): string {
  return `${value.toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })}%`;
}

export function formatSnapshotDate(value: string | undefined): string {
  if (!value) return "data não informada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "data não informada";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

export function activeFilterCount(filters: EngagementFilters): number {
  return Object.values(filters).filter(Boolean).length;
}

function buildEngagementBuckets(rows: EngagementStudent[]): BreakdownItem[] {
  const buckets = [
    { label: "Sem atividade", test: (value: number) => value <= 0 },
    { label: "Baixo (até 1)", test: (value: number) => value > 0 && value <= 1 },
    { label: "Intermediário (1–2)", test: (value: number) => value > 1 && value <= 2 },
    { label: "Alto (acima de 2)", test: (value: number) => value > 2 }
  ];
  const values = rows.map((row) => numeric(row.rating_engajamento) ?? 0);
  return buckets.map((bucket) => {
    const value = values.filter(bucket.test).length;
    return { label: bucket.label, value, share: percent(value, values.length) };
  });
}

function buildRecencyBuckets(rows: EngagementStudent[], generatedAt?: string): BreakdownItem[] {
  const reference = parseDate(generatedAt) || latestLogin(rows);
  const buckets = [
    { label: "Até 7 dias", value: 0 },
    { label: "8 a 30 dias", value: 0 },
    { label: "31 a 60 dias", value: 0 },
    { label: "Mais de 60 dias", value: 0 },
    { label: "Sem acesso", value: 0 }
  ];

  for (const row of rows) {
    const login = parseDate(row.ultimo_login);
    if (!login || !reference) {
      buckets[4].value += 1;
      continue;
    }
    const days = Math.max(0, Math.floor((reference.getTime() - login.getTime()) / 86_400_000));
    if (days <= 7) buckets[0].value += 1;
    else if (days <= 30) buckets[1].value += 1;
    else if (days <= 60) buckets[2].value += 1;
    else buckets[3].value += 1;
  }

  return buckets.map((item) => ({ ...item, share: percent(item.value, rows.length) }));
}

function latestLogin(rows: EngagementStudent[]): Date | null {
  const dates = rows.map((row) => parseDate(row.ultimo_login)).filter((date): date is Date => date !== null);
  if (!dates.length) return null;
  return new Date(Math.max(...dates.map((date) => date.getTime())));
}

function groupRows(rows: EngagementStudent[], selector: (row: EngagementStudent) => string): Map<string, EngagementStudent[]> {
  const groups = new Map<string, EngagementStudent[]>();
  rows.forEach((row) => {
    const key = selector(row);
    const group = groups.get(key);
    if (group) group.push(row);
    else groups.set(key, [row]);
  });
  return groups;
}

function countStatus(rows: EngagementStudent[], status: EngagementStatus): number {
  return rows.filter((row) => studentStatus(row) === status).length;
}

function privacySafeOptions(rows: EngagementStudent[], selector: (row: EngagementStudent) => string): EngagementOption[] {
  return [...groupRows(rows, selector).entries()]
    .filter(([, group]) => group.length >= MIN_AGGREGATE_SIZE)
    .map(([label]) => label)
    .sort(localeSort)
    .map(toOption);
}

function toOption(value: string): EngagementOption {
  return { value, label: value };
}

function periodRank(value: string): number {
  const [year, semester] = value.split("/").map(Number);
  return (year || 0) * 2 + (semester || 0);
}

function cleanText(value: unknown, fallback: string): string {
  const cleaned = String(value ?? "").replace(/\s+/g, " ").trim();
  return cleaned || fallback;
}

function normalizeText(value: unknown): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase("pt-BR");
}

function isIncluded(value: boolean | number | string | null | undefined): boolean {
  return value === true || value === 1 || normalizeText(value) === "true";
}

function numeric(value: NumericValue): number | null {
  if (value === null || value === undefined || value === "") return null;
  const result = Number(value);
  return Number.isFinite(result) ? result : null;
}

function parseDate(value: unknown): Date | null {
  if (!value) return null;
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
}

function percent(value: number, total: number): number {
  return total > 0 ? (value / total) * 100 : 0;
}

function localeSort(a: string, b: string): number {
  return a.localeCompare(b, "pt-BR");
}
